import _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { StripeCardInput, useCreateBillingPortalMutation } from '@playbookux/playbook-web-core';

import { daysRemainingTrialPlan } from '../../../../core/utils/days-remaining-trial-plan';
import { formatPlanName } from '../../../../core/utils/format-plan-name';
import Card from './card-element';
import PrepaidBalanceModal from './prepaid-balance-modal';
import ReceiptTable from './custom-table';
import { config } from '../../../../config';

import {
  Button,
  ButtonContainer,
  Checkbox,
  CreateCardContainer,
  ErrorMessage,
  HeaderContent,
  Link,
  Loader,
  Message,
  SavedCardsMessage,
  Section,
  Subtitle,
  Title,
} from './styles';

import {
  CREATE_PAYMENT_METHOD,
  CREATE_PREPAID_BALANCE,
  GET_COMPANY_BY_ID,
  GET_COMPANY_RECEIPTS,
  GET_SUBSCRIPTIONS,
  REMOVE_PAYMENT_METHOD,
  SAVED_CARDS,
  UPDATE_DEFAULT_PAYMENT_METHOD,
  updateDefaultPaymentMethodUpdated,
  updatePaymentMethodCreated,
  updatePaymentMethodRemoved,
  VERIFY_CAPTCHA,
} from '../graphql';

const Billing = (props) => {
  const { companyId } = props;
  const recaptchaRef = useRef();
  const isRecaptchaEnabled = config.REACT_APP_RECAPTCHA_ENABLED === 'true';

  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(!isRecaptchaEnabled || false);

  const { data: { company } = {} } = useQuery(GET_COMPANY_BY_ID, {
    variables: { id: companyId },
    skip: !companyId,
  });

  const { data: { subscriptions } = {}, loading: isLoadingSubs } = useQuery(GET_SUBSCRIPTIONS, {
    variables: { companyId },
    skip: !companyId,
  });

  const { data: { paymentMethods } = {}, loading: isLoadingCards } = useQuery(SAVED_CARDS, {
    variables: { companyId },
    skip: !companyId,
  });

  const { data: { receipts } = {}, loading: isLoadingReceipts } = useQuery(GET_COMPANY_RECEIPTS, {
    variables: { companyId },
    skip: !companyId,
  });

  const {
    loading: createPortalLoading,
    createPortal,
  } = useCreateBillingPortalMutation();

  const [updateDefaultPaymentMethod] = useMutation(UPDATE_DEFAULT_PAYMENT_METHOD, {
    update: updateDefaultPaymentMethodUpdated(companyId),
  });

  const [removePaymentMethod] = useMutation(REMOVE_PAYMENT_METHOD, {
    update: updatePaymentMethodRemoved(companyId),
  });

  const [createPaymentMethod] = useMutation(CREATE_PAYMENT_METHOD, {
    update: updatePaymentMethodCreated(companyId),
  });

  const [createPrepaidBalance] = useMutation(CREATE_PREPAID_BALANCE);

  const [verifyCaptcha] = useMutation(VERIFY_CAPTCHA);

  const handleBillingPortal = (customerId) => {
    createPortal(customerId)
      .then((response) => {
        if (response) {
          window.open(response);
        }
      });
  };

  const handleCreateCard = async () => {
    setLoading(true);

    if (!isCaptchaVerified && isRecaptchaEnabled) {
      setError('Something wrong with card or captcha, try again');
      setLoading(false);
      return;
    }

    try {
      setError(null);
      await createPaymentMethod({
        variables: {
          companyId,
          token,
          isDefault,
        },
      });

      setLoading(false);
      setIsDefault(false);
      setShowInput(false);
      setCompleted(false);
      setIsCaptchaVerified(!isRecaptchaEnabled || false);
    } catch (err) {
      const graphQLError = _.first(err.graphQLErrors);
      const message = graphQLError ? graphQLError.message : err.message;

      setError(message);
      setLoading(false);
      setCompleted(false);
      setIsCaptchaVerified(!isRecaptchaEnabled || false);
    }
  };

  const handleRemove = async (paymentMethodId) => {
    setLoading(true);

    try {
      await removePaymentMethod({
        variables: {
          companyId,
          paymentMethodId,
        },
      });

      setError(null);
      setLoading(false);
    } catch (err) {
      const graphQLError = _.first(err.graphQLErrors);
      const message = graphQLError ? graphQLError.message : err.message;

      setLoading(false);
      setError(message);
    }
  };

  const handleUpdateDefaultPaymentMethod = async (paymentMethodId) => {
    setLoading(true);

    try {
      await updateDefaultPaymentMethod({
        variables: {
          companyId,
          paymentMethodId,
        },
      });

      setError(null);
      setLoading(false);
    } catch (err) {
      const graphQLError = _.first(err.graphQLErrors);
      const message = graphQLError ? graphQLError.message : err.message;

      setLoading(false);
      setError(message);
    }
  };

  const handleStripeCallback = (newToken) => {
    setToken(newToken);
  };

  const handleShowInput = () => {
    setShowInput(!showInput);
  };

  const handleButtonCancel = () => {
    setError(null);
    setSubmitError(null);
    setLoading(false);
    setIsDefault(false);
    setShowInput(false);
    setCompleted(false);
    setIsCaptchaVerified(!isRecaptchaEnabled || false);
  };

  const handleCompleted = (isCompleted) => {
    setCompleted(isCompleted);
  };

  const handleIsDefault = () => {
    setIsDefault(!isDefault);
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleSubmitPrepaidBalance = async (amount, rememberCard, id) => {
    setSubmitLoading(true);

    try {
      await createPrepaidBalance({
        variables: {
          companyId,
          prepaidAmount: amount,
          token,
          paymentMethodId: id,
          rememberCard,
        },
      });

      setShowModal(false);
      setSubmitLoading(false);
      setSubmitError(null);
    } catch (err) {
      const graphQLError = _.first(err.graphQLErrors);
      const message = graphQLError ? graphQLError.message : err.message;

      setSubmitLoading(false);
      setSubmitError(message);
    }
  };

  const handleCaptchaVerify = async (responsetoken) => {
    try {
      const { data } = await verifyCaptcha({
        variables: {
          token: responsetoken,
        },
      });

      const { verifyCaptcha: { success } } = data;

      if (!success) {
        setError('Something wrong with captcha, try again');
        throw new Error('Something wrong with captcha, try again');
      }
      setIsCaptchaVerified(true);
    } catch (e) {
      setError('Something wrong with captcha, try again');
      recaptchaRef.current.reset();
    }
  };

  const getTitleButton = () => (
    !_.isEmpty(subscriptions) ? 'Manage' : 'Upgrade'
  );

  if (isLoadingSubs || isLoadingCards || isLoadingReceipts) {
    return (
      <Loader data-loader="billing-loader" />
    );
  }

  const { balanceRemaining, stripeCustomerId = '', plan = '' } = company ?? {};
  const prepaidBalance = ((balanceRemaining || 0) / 100);

  const subscription = _.first(subscriptions);
  const daysRemaining = daysRemainingTrialPlan(subscription);

  const hasSavedCards = !_.isEmpty(paymentMethods);

  return (
    <>
      <Title as="h2">Billing</Title>

      <Section addBorder>
        <HeaderContent>
          <Subtitle>Subscription Plan</Subtitle>
          <Button
            disabled={createPortalLoading}
            loading={createPortalLoading}
            onClick={() => handleBillingPortal(stripeCustomerId)}
            type="submit"
          >
            {getTitleButton(stripeCustomerId)}
          </Button>
        </HeaderContent>
        <Message>
          Update subscription settings by clicking the &quot;Manage&quot; button
        </Message>
        {daysRemaining && (
          <Message daysLeft>
            {`${daysRemaining} ${daysRemaining > 1 ? 'days' : 'day'} left in '${formatPlanName(plan)}' Plan trial`}
          </Message>
        )}
      </Section>

      <Section addBorder>
        <HeaderContent>
          <Subtitle>Prepaid Balance</Subtitle>

          <Button onClick={handleOpenModal}>
            Add Prepaid Balance
          </Button>

          <PrepaidBalanceModal
            error={submitError}
            onClose={setShowModal}
            callback={handleStripeCallback}
            onSubmit={handleSubmitPrepaidBalance}
            open={showModal}
            paymentMethods={paymentMethods}
            loading={submitLoading}
          />
        </HeaderContent>
        <Message>
          Prepay for studies so you don&lsquo;t have to enter a credit card at checkout.
        </Message>
        <Message>
          Prepaid Balance Remaining: $
          {prepaidBalance.toLocaleString()}
        </Message>
      </Section>

      <Title as="h2">Saved credit and debit cards</Title>

      <Section>
        {!hasSavedCards && (
          <SavedCardsMessage data-cy="empty-card-list">
            You don’t have any saved cards.
          </SavedCardsMessage>
        )}

        {loading && (
          <Loader data-loader="card-list-loader" />
        )}

        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}

        {!loading && (
          <>
            {!error && hasSavedCards && _.map(paymentMethods, (newPaymentMethod) => (
              <Card
                key={newPaymentMethod.id}
                error={error}
                onRemove={handleRemove}
                onUpdate={handleUpdateDefaultPaymentMethod}
                paymentMethod={newPaymentMethod}
              />
            ))}

            {!showInput && (
              <Link onClick={handleShowInput}>
                + Add a card
              </Link>
            )}

            {showInput && (
              <CreateCardContainer>
                <>
                  <StripeCardInput
                    disabled={!showInput}
                    onSetCompleted={handleCompleted}
                    callback={handleStripeCallback}
                  />
                </>

                {hasSavedCards && (
                  <Checkbox
                    name="set_as_default"
                    label="Set as default payment method"
                    checked={isDefault}
                    disabled={!completed}
                    onChange={handleIsDefault}
                  />
                )}
                {isRecaptchaEnabled && (
                  <CreateCardContainer>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={config.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY_V2}
                      onChange={handleCaptchaVerify}
                    />
                  </CreateCardContainer>
                )}

                <ButtonContainer>
                  <Button.Inverted onClick={handleButtonCancel}>
                    Cancel
                  </Button.Inverted>
                  <Button
                    disabled={!completed || !token || (!isCaptchaVerified && isRecaptchaEnabled)}
                    onClick={handleCreateCard}
                  >
                    Save Card
                  </Button>
                </ButtonContainer>
              </CreateCardContainer>
            )}
          </>
        )}
      </Section>

      <Title as="h2">Receipts</Title>

      <Section>
        {_.isEmpty(receipts) ? (
          <SavedCardsMessage>
            You don’t have any receipts
          </SavedCardsMessage>
        ) : <ReceiptTable receipts={receipts} />}
      </Section>
    </>
  );
};

Billing.propTypes = {
  companyId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  connect(mapStateToProps, null),
);

export default enhance(Billing);
